"use client";

import {
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import Carousel from "@/components/carousel/Carousel";
import {
  carouselOptions,
  normalizeCarousel,
  type CarouselSettings,
} from "@/components/carousel/carousel-settings";
import type { PurposeItem, PurposeSettings } from "./model";
import { safeUrl } from "./model";
import type { CtaComponentProps } from "@/cms/models";
import RippleCtaButtonComponent from "@/components/ui/RippleCtaButtonComponent";
import CaseMediaViewer from "@/components/ui/CaseMediaViewer";
import { useMediaDescriptions } from "@/components/ui/MediaImage";
import { resolveMediaAlt } from "@/utils/media-descriptions";
import { inlinePlainText } from "@/utils/inline-text";
import InlineText, { inlineTextStyle } from "@/components/ui/InlineText";

const GALLERY_CAROUSEL = carouselOptions("gallery", 2)!;

/** The fold in px: the computed max-height while folded (collapseHeight in any absolute unit); open, it has none, so the
 *  last fold stands. */
export function foldLimit(maxHeight: string, previous: number): number {
  return /^\d+(?:\.\d+)?px$/.test(maxHeight)
    ? Number.parseFloat(maxHeight)
    : previous;
}

/** Clipped from the first paint; the toggle appears once the text is known to be longer than the clip. `context` follows
 *  the label for screen readers only, so a page of toggles is not a list of identical names. Focus moving into the
 *  clipped text, such as a tab to a link in it, opens it. */
export function CollapsibleText({
  children,
  moreLabel = "Læs mere",
  lessLabel = "Vis mindre",
  context,
}: {
  children: ReactNode;
  moreLabel?: string;
  lessLabel?: string;
  context?: string;
}) {
  const id = useId();
  const body = useRef<HTMLDivElement>(null);
  const limit = useRef(200);
  const [overflows, setOverflows] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const element = body.current;
    if (!element) return;
    const measure = () => {
      limit.current = foldLimit(
        getComputedStyle(element).maxHeight,
        limit.current,
      );
      setOverflows(element.scrollHeight > Math.ceil(limit.current));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      className={`pf-collapse ${open ? "" : "is-collapsed"} ${overflows ? "has-overflow" : ""}`}
    >
      <div
        className="pf-collapse-body"
        id={id}
        ref={body}
        onFocus={() => setOpen(true)}
      >
        {children}
      </div>
      {overflows && (
        <button
          type="button"
          className="pf-collapse-toggle"
          aria-expanded={open}
          aria-controls={id}
          onClick={() => setOpen(!open)}
        >
          {open ? lessLabel : moreLabel}
          {context && <span className="sr-only">{context}</span>}
        </button>
      )}
    </div>
  );
}

export function Questions({
  items,
  settings,
}: {
  items: PurposeItem[];
  settings: PurposeSettings;
}) {
  const id = useId();
  const [open, setOpen] = useState<Set<number>>(
    () => new Set(settings.openFirst === false ? [] : [0]),
  );
  return (
    <div className="pf-list">
      {items.map((item, index) => (
        <div className="pf-faq pf-items" key={item.id || index}>
          <button
            type="button"
            className="pf-question"
            aria-expanded={open.has(index)}
            aria-controls={`${id}-${index}`}
            onClick={() =>
              setOpen((current) => {
                const next =
                  settings.allowMultiple === false
                    ? new Set<number>()
                    : new Set(current);
                if (current.has(index)) next.delete(index);
                else next.add(index);
                return next;
              })
            }
          >
            <span style={inlineTextStyle(item.title)}>
              <InlineText value={item.title} />
            </span>
            <span aria-hidden>{open.has(index) ? "−" : "+"}</span>
          </button>
          <div
            id={`${id}-${index}`}
            className="pf-answer"
            hidden={!open.has(index)}
          >
            <div
              className="styled-description"
              dangerouslySetInnerHTML={{ __html: item.text || "" }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
/** `videos[i]` is item i's player, rendered on the server; it stands in the picture's place and is not in the lightbox.
 *  Billedserie (`variant: 2`) is the shared carousel with `carousel` (the section's own keys read by normalizeCarousel). */
export function PhotoGallery({
  items,
  settings,
  videos = [],
  carousel,
}: {
  items: PurposeItem[];
  settings: PurposeSettings;
  videos?: ReactNode[];
  carousel?: CarouselSettings;
}) {
  const [index, setIndex] = useState<number | null>(null);
  const descriptions = useMediaDescriptions();
  const entries = items.flatMap((item, i) =>
    videos[i] || safeUrl(item.imageUrl) ? [{ item, video: videos[i] }] : [],
  );
  const images = entries.filter((entry) => !entry.video).map((e) => e.item);
  const series = settings.variant === 2;
  const altOf = (item: PurposeItem) =>
    resolveMediaAlt(descriptions, item.imageUrl ?? "", "");
  const picture = (item: PurposeItem) => {
    const i = images.indexOf(item);
    const alt = altOf(item);
    const image = (
      <img
        className="pf-image"
        src={item.imageUrl}
        alt={alt}
        style={
          item.imagePosition
            ? { objectPosition: item.imagePosition }
            : undefined
        }
        {...(series ? { "data-carousel-media": "" } : {})}
      />
    );
    const href = safeUrl(item.link);
    if (href)
      return (
        <a
          className="pf-gallery-link"
          href={href}
          target={item.openInNewTab ? "_blank" : undefined}
          rel={item.openInNewTab ? "noopener noreferrer" : undefined}
          aria-label={alt ? undefined : item.title || `Billede ${i + 1}`}
        >
          {image}
        </a>
      );
    return settings.lightbox === false ? (
      image
    ) : (
      <button
        type="button"
        aria-label={`Åbn ${alt || `billede ${i + 1}`}`}
        onClick={() => setIndex(i)}
      >
        {image}
      </button>
    );
  };
  const figures = entries.map(({ item, video }, i) => (
    <figure className="pf-items" key={item.id || i}>
      {video ||
        (series ? (
          <div className="pf-gallery-frame">{picture(item)}</div>
        ) : (
          picture(item)
        ))}
      {settings.showCaptions && item.text && (
        <figcaption className="pf-itemText">{item.text}</figcaption>
      )}
    </figure>
  ));
  return (
    <>
      {series ? (
        <Carousel
          settings={carousel ?? normalizeCarousel({}, GALLERY_CAROUSEL)}
          label={inlinePlainText(settings.heading) || "Billeder"}
          noun={{ one: "billede", many: "billeder" }}
          className="pf-gallery pf-gallery-series"
        >
          {figures}
        </Carousel>
      ) : (
        <div
          className={`pf-gallery pf-grid ${settings.variant === 1 ? "pf-gallery-mosaic" : ""}`}
        >
          {figures}
        </div>
      )}
      {index !== null && (
        <CaseMediaViewer
          caseData={{
            images: [],
            imageUrl: "",
            videoUrls: [],
            advertisementAddressOrDisplayName:
              inlinePlainText(settings.heading) || "Galleri",
            shortAddress: "",
          }}
          variant="slideshow"
          view="photo"
          initialIndex={index}
          media={images.map((item, i) => ({
            type: "image",
            image: {
              mdUrl: item.imageUrl!,
              lgUrl: item.imageUrl!,
              xlUrl: item.imageUrl!,
              namespace: "photo",
              pixelHeight: 0,
              pixelWidth: 0,
              description: altOf(item),
              priority: i,
            },
          }))}
          onClose={() => setIndex(null)}
        />
      )}
    </>
  );
}
/** The dialog is portalled outside the section, so the section's form styles (Udseende › Formular) are handed to it;
 *  `trigger` is the section's purpose button (its wrapper class and ripple button settings), which opens it. */
export function EventRegistration({
  label,
  trigger,
  dialogClassName = "",
  dialogStyle,
  children,
}: {
  label: string;
  trigger: { id: string; className: string; config: CtaComponentProps };
  dialogClassName?: string;
  dialogStyle?: CSSProperties;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <span className={trigger.className}>
        <RippleCtaButtonComponent
          id={trigger.id}
          itemIndex={0}
          config={trigger.config}
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
        />
      </span>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        className={`pf pf-event-dialog ${dialogClassName}`.trim()}
        style={dialogStyle}
        data-purpose-family="event-dialog"
      >
        <div className="pf-modal-backdrop">
          <DialogPanel className="pf-modal">
            <div className="pf-modal-heading">
              <DialogTitle>{label}</DialogTitle>
              <button
                type="button"
                aria-label="Luk"
                onClick={() => setOpen(false)}
              >
                ×
              </button>
            </div>
            {children}
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
}

export { default as PurposePropertyRow } from "@/components/PropertyRowCard";
// The gallery card keeps its own loading state and has no "use client" of its own.
export { default as PurposePropertyGallery } from "@/components/PropertyGalleryCard";

/** Tabs over server-rendered property lists; arrow keys move between tabs as in the WAI-ARIA tabs pattern. With one
 *  intro per tab only the selected tab's intro is in the DOM, so the page never holds two section headings. `reveal`
 *  makes the tab row a reveal of its own, for the card layout on the look system. */
export function PropertyTabs({
  labels,
  intro,
  pill = false,
  reveal = false,
  children,
}: {
  labels: string[];
  intro: ReactNode | ReactNode[];
  pill?: boolean;
  reveal?: boolean;
  children: ReactNode[];
}) {
  const id = useId();
  const [active, setActive] = useState(0);
  const select = (index: number) => {
    const next = (index + labels.length) % labels.length;
    setActive(next);
    document.getElementById(`${id}-tab-${next}`)?.focus();
  };
  return (
    <>
      <div className="pf-tabs-head">
        <div className="pf-tabs-intro">
          {Array.isArray(intro) ? intro[active] : intro}
        </div>
        <div
          className={`pf-tabs ${pill ? "pf-tabs-pill" : ""}`}
          role="tablist"
          data-re-reveal={reveal ? "" : undefined}
        >
          {labels.map((label, index) => (
            <button
              key={index}
              type="button"
              role="tab"
              id={`${id}-tab-${index}`}
              aria-selected={active === index}
              aria-controls={`${id}-panel-${index}`}
              tabIndex={active === index ? 0 : -1}
              className="pf-tab"
              onClick={() => setActive(index)}
              onKeyDown={(event) => {
                if (event.key === "ArrowRight") select(index + 1);
                else if (event.key === "ArrowLeft") select(index - 1);
                else if (event.key === "Home") select(0);
                else if (event.key === "End") select(labels.length - 1);
                else return;
                event.preventDefault();
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {children.map((panel, index) => (
        <div
          key={index}
          role="tabpanel"
          id={`${id}-panel-${index}`}
          aria-labelledby={`${id}-tab-${index}`}
          hidden={active !== index}
        >
          {panel}
        </div>
      ))}
    </>
  );
}
